import {LazyQueryExecFunction, OperationVariables, QueryResult} from "@apollo/client";
import isEmpty from "lodash/isEmpty";
import {useEffect, useRef, useState} from "react";
import {GroupBase, OptionsOrGroups} from "react-select";

interface UseLoadOptionsParams<
  Option,
  ResponseType,
  IsGrouped extends boolean = true,
  Fetch extends LazyQueryExecFunction<ResponseType, OperationVariables> = LazyQueryExecFunction<
    ResponseType,
    OperationVariables
  >,
> {
  options?: IsGrouped extends true ? OptionsOrGroups<Option, GroupBase<Option>> : Option[];
  fetchOptions: Fetch;
  transform: (
    result: QueryResult<ResponseType, OperationVariables>
  ) => IsGrouped extends true ? OptionsOrGroups<Option, GroupBase<Option>> : Option[];
}

// This hook is used to load options for a select component asynchronously.
// Most of our select options come from the backend, so this hook is designed to work with GQL to get them.

//Option is the type of the options to be loaded, ResponseType is the type of the response from the GQL query,
// IsGrouped is a boolean that indicates if the options are grouped or not, and Fetch is the type of the function that fetches the options.
export function useLoadOptions<
  Option,
  ResponseType,
  IsGrouped extends boolean = true,
  Fetch extends LazyQueryExecFunction<ResponseType, OperationVariables> = LazyQueryExecFunction<
    ResponseType,
    OperationVariables
  >,
>({options, fetchOptions, transform: _transform}: UseLoadOptionsParams<Option, ResponseType, IsGrouped, Fetch>) {
  const [loadedOptions, setLoadedOptions] = useState<
    (IsGrouped extends true ? OptionsOrGroups<Option, GroupBase<Option>> : Option[]) | undefined
  >(options);
  const [isLoading, setIsLoading] = useState(false);

  // transform function will not change but could be passed in not memoized
  // so if use it directly and we include it in the dependency array of the useEffect,
  // it will cause an infinite loop as the parent rerenders when the options are passed out.
  // so we use a ref to keep the latest version of the transform function and not rerender each time the parent rerenders.
  const transformRef = useRef(_transform);
  transformRef.current = _transform;

  useEffect(() => {
    // Ignore results from a superseded fetch: when fetchOptions identity changes (e.g. a new search
    // term or location set) a fresh load starts, and responses can otherwise resolve out of order and
    // overwrite the latest with stale data. Also prevents setState after unmount.
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const result = await fetchOptions();
        if (!cancelled) {
          setLoadedOptions(transformRef.current(result));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    if (!options || isEmpty(options)) {
      load();
    } else {
      setLoadedOptions(options);
    }

    return () => {
      cancelled = true;
    };
  }, [options, fetchOptions]);

  return {
    options: loadedOptions ?? [],
    isLoading: loadedOptions === null || isLoading,
    setOptions: setLoadedOptions,
  };
}
